/**
 * Body archetypes — docs/02-movement.md §3.2.
 *
 * "Body plan is not a skin." Every archetype here rewrites the base movement
 * package (docs/02 §2) into a genuinely different set of platforming problems.
 * That is the replayability engine: the world feels different to move through,
 * not merely different to stat-check.
 *
 * This module is pure data + resolution. It imports nothing from combat/, world/
 * or entity/ — the movement controller consumes `resolveMovement()` and the
 * combat/status systems consume `systemicBias`, by plain string ids.
 */

// --------------------------------------------------------------- traversal

/**
 * The nine gated traversal abilities, in the intended critical-path order
 * (docs/02 §3.1). Order is a level-design constraint, so it lives in one place
 * and everything else — attunement, gating, UI — reads it from here.
 */
export const TRAVERSAL_ORDER = [
  'airstep',
  'cling',
  'breach',
  'tether',
  'burrow',
  'blink',
  'updraft',
  'fluid-adapt',
  'phase-drift',
] as const;

export type TraversalAbilityId = (typeof TRAVERSAL_ORDER)[number];

export interface TraversalAbilityDef {
  id: TraversalAbilityId;
  name: string;
  /** Level-design gate this ability opens. */
  gates: string;
  /** The material-sim hook. Every ability must have one (docs/02 §3 rule 4). */
  materialInteraction: string;
}

export const TRAVERSAL_ABILITIES: readonly TraversalAbilityDef[] = [
  {
    id: 'airstep',
    name: 'Airstep',
    gates: 'wide gaps, 3-cell ceiling gaps',
    materialInteraction: '6 frames of ignition immunity when stepping through flame',
  },
  {
    id: 'cling',
    name: 'Cling',
    gates: 'vertical shafts',
    materialInteraction: 'cannot grip ice, oiled or corroding surfaces',
  },
  {
    id: 'breach',
    name: 'Breach',
    gates: 'cracked walls, brittle floors',
    materialInteraction: 'destroys hardness <= 3, shatters FROZEN, detonates pressure vessels',
  },
  {
    id: 'tether',
    name: 'Tether',
    gates: 'chasms, ceiling routes',
    materialInteraction: 'conducts electricity; burns if wet with oil',
  },
  {
    id: 'burrow',
    name: 'Burrow',
    gates: 'sand seals, buried routes',
    materialInteraction: 'displaces loose cells; drowns you in liquid-adjacent sand',
  },
  {
    id: 'blink',
    name: 'Blink',
    gates: 'thin sealed walls',
    materialInteraction: 'leaves TEMPORAL_RESIDUE; chills the matter blinked through',
  },
  {
    id: 'updraft',
    name: 'Updraft',
    gates: 'vertical ascents',
    materialInteraction: 'requires fire — you make the fire and ride the thermal',
  },
  {
    id: 'fluid-adapt',
    name: 'Fluid Adapt',
    gates: 'flooded regions',
    materialInteraction: 'immunity to viscosity, not to acid or lava',
  },
  {
    id: 'phase-drift',
    name: 'Phase Drift',
    gates: 'sealed vaults',
    materialInteraction: 'cannot phase through ADAMANT_SEAL or VOID_MATTER',
  },
];

/**
 * The catalogue by id. Read by `core/runSetupAdapter.ts` for the labels the
 * run-setup screen prints; before #118 it was read only by a type guard nothing
 * called, and the screen hand-copied the names and gates instead.
 *
 * `isTraversalAbility`, that guard, was deleted rather than wired. Nothing in
 * the codebase narrows a raw string to a `TraversalAbilityId`: the two paths
 * that could — `Progression.effectiveStartAbilities`, which filters stored
 * picks against `attunedAbilities()`, and `grantAbility`, which takes the union
 * type and tolerates a stranger — are both already stricter than a membership
 * test would be. A guard whose check is subsumed everywhere it could be applied
 * is not an unwired feature, it is a spare part.
 */
export const TRAVERSAL_BY_ID: Readonly<Record<string, TraversalAbilityDef>> =
  Object.fromEntries(TRAVERSAL_ABILITIES.map((a) => [a.id, a]));

// -------------------------------------------------------------- modifiers

/** How the dash resolves. Chrono-monk's dash is not a translation at all. */
export type DashMode = 'translate' | 'rewind';

/** What the body does instead of (or in addition to) a second jump. */
export type VerticalMode = 'double-jump' | 'hover-thrust' | 'flight' | 'none';

/**
 * Deltas applied to the base movement package. Multipliers are `*Mul` and
 * default to 1; additive counts default to 0. The controller resolves
 * base × modifiers once at run start, never per-frame — these are cold data.
 */
export interface MovementModifiers {
  // horizontal
  maxRunSpeedMul: number;
  groundAccelMul: number;
  airAccelMul: number;
  turnAccelMul: number;
  massMul: number;
  groundFrictionMul: number;

  // vertical
  jumpHeightMul: number;
  riseGravityMul: number;
  fallGravityMul: number;
  maxFallSpeedMul: number;
  verticalMode: VerticalMode;
  /** Seconds of flight/hover fuel. 0 disables the meter entirely. */
  flightSeconds: number;
  /** Seconds of thrust before overheat. 0 = no thermal model on this body. */
  thermalSeconds: number;

  // dash
  dashSpeedMul: number;
  dashDurationMul: number;
  dashCooldownMul: number;
  /** Extra mid-air dashes once Airstep is found (Airstep itself grants 1). */
  airDashBonus: number;
  dashMode: DashMode;

  // walls
  wallSlideSpeedMul: number;
  canWallSlide: boolean;
  /** Cling-grade indefinite grip without owning the Cling ability. */
  canWallCling: boolean;

  // fluids
  /** 0 = full viscosity penalty, 1 = Fluid-Adapt-grade immunity. */
  viscosityImmunity: number;

  // shape
  hitboxWidthMul: number;
  hitboxHeightMul: number;
  /** Narrowest gap (cells) the body fits through. Base 10w capsule => 10. */
  minGapCells: number;
  /** Moves through loose solids without owning Burrow. */
  passesLooseSolids: boolean;
  /** Downhill acceleration bias; negative values also mean uphill drag. */
  slopeBias: number;

  // multiplicity
  /** Independently-simulated bodies sharing one controller. */
  bodyCount: number;
  /**
   * Chain segments for anchor-and-stretch bodies — issue #186.
   *
   * Documentation and a validation cross-check, not the render's source of
   * truth: the actual chain is however many `tail`/`tailLow`/`tailTip` bones
   * the archetype's `.plan` declares (`archetypeBody.ts`'s `plan` id), because
   * that is the number `poseSkeleton` and `twoBoneIK` genuinely read. A second,
   * independent "3" here that the plan could silently drift out of step with
   * would be worse than no number at all — `tests/archetypeBody.test.ts`
   * asserts this against `quadruped.plan`'s own bone count for exactly that
   * reason. Only Segmented biomech authors it away from the base `1`.
   */
  segmentCount: number;

  /** Named stances the body can swap between. Empty = single stance. */
  stances: readonly string[];

  /** Issue #186. See `entity/movementTuning.ts`'s own field for the mechanic. */
  tailAnchorReach: boolean;

  /** Peak run-speed bonus at 0 HP, e.g. 0.35 = +35%. */
  speedPerMissingHp: number;
}

/** Neutral package — the Duelist baseline, before any archetype delta. */
export const BASE_MOVEMENT_MODIFIERS: MovementModifiers = {
  maxRunSpeedMul: 1,
  groundAccelMul: 1,
  airAccelMul: 1,
  turnAccelMul: 1,
  massMul: 1,
  groundFrictionMul: 1,

  jumpHeightMul: 1,
  riseGravityMul: 1,
  fallGravityMul: 1,
  maxFallSpeedMul: 1,
  verticalMode: 'double-jump',
  flightSeconds: 0,
  thermalSeconds: 0,

  dashSpeedMul: 1,
  dashDurationMul: 1,
  dashCooldownMul: 1,
  airDashBonus: 0,
  dashMode: 'translate',

  wallSlideSpeedMul: 1,
  canWallSlide: true,
  canWallCling: false,

  viscosityImmunity: 0,

  hitboxWidthMul: 1,
  hitboxHeightMul: 1,
  minGapCells: 10,
  passesLooseSolids: false,
  slopeBias: 0,

  bodyCount: 1,
  segmentCount: 1,

  stances: [],
  tailAnchorReach: false,

  speedPerMissingHp: 0,
};

// ----------------------------------------------------------- survivability

export type DamageModel =
  | 'single'
  /** One HP pool spread over many discrete bodies (drone swarm). */
  | 'shared-pool'
  /** Each body has its own HP; losing one is a partial loss (parasite colony). */
  | 'per-body'
  /** Segments are destroyed head-to-tail (segmented biomech). */
  | 'segmented';

export interface Revival {
  kind: 'none' | 'reassemble' | 'respawn-body' | 'restance';
  charges: number;
  scope: 'never' | 'room' | 'run';
  note: string;
}

export interface Survivability {
  hpMul: number;
  damageModel: DamageModel;
  revival: Revival;
  /** Drowning applies. Possessed armor is immune but also cannot swim out. */
  drowns: boolean;
  /** Poise/stagger resistance multiplier — mass usually implies this. */
  poiseMul: number;
}

const NO_REVIVAL: Revival = { kind: 'none', charges: 0, scope: 'never', note: '' };

// -------------------------------------------------------------- systemic

/**
 * Material-interaction bias. Keys are plain damage-class / status ids so this
 * module stays decoupled from combat/ and sim/ — the integrator maps them.
 * Absent key = 1.0 (no bias). Susceptibility from *composition* is derived
 * elsewhere (docs/03 §6); this is the body-plan bias layered on top.
 */
export interface SystemicBias {
  damageTaken: Readonly<Record<string, number>>;
  statusTaken: Readonly<Record<string, number>>;
  /** Material tag names this body refuses to grip, enter or survive. */
  refuses: readonly string[];
  note: string;
}

const NO_BIAS: SystemicBias = { damageTaken: {}, statusTaken: {}, refuses: [], note: '' };

// ------------------------------------------------------------- archetypes

export interface ArchetypeDef {
  id: string;
  name: string;
  kind: 'humanoid' | 'weird';
  /** Deltas from the base package. Everything omitted is baseline. */
  movement: Partial<MovementModifiers>;
  /** Traversal ability ids granted at run start, free. */
  innateAbilities: readonly TraversalAbilityId[];
  survivability: Survivability;
  systemicBias: SystemicBias;
  /** The mechanical hook — the one sentence that makes this body a puzzle. */
  special: string;
  /**
   * Issue #185. Per-stance deltas from `movement`'s own resolved tuning, keyed
   * by an id in `movement.stances`. `movement`'s authored values are the
   * *midpoint* for a body that stances — the delta this table adds is stacked
   * on top of it, the same way `movement` itself stacks on
   * `BASE_MOVEMENT_MODIFIERS`. Absent for every archetype with an empty
   * `stances` array; `resolveStance` returns `{}` for those, a no-op through
   * `applyModifiers`.
   */
  stanceProfiles?: Readonly<Record<string, Partial<MovementModifiers>>>;
}

const STD: Survivability = {
  hpMul: 1,
  damageModel: 'single',
  revival: NO_REVIVAL,
  drowns: true,
  poiseMul: 1,
};

function surv(over: Partial<Survivability>): Survivability {
  return { ...STD, ...over };
}

export const ARCHETYPES: readonly ArchetypeDef[] = [
  {
    id: 'duelist',
    name: 'Duelist',
    kind: 'humanoid',
    // Baseline by definition — the tuning reference every other body is read against.
    movement: { airDashBonus: 1 },
    innateAbilities: [],
    survivability: surv({}),
    systemicBias: NO_BIAS,
    special: 'Two air dashes instead of one once Airstep is found; the yardstick body.',
  },
  {
    id: 'breaker',
    name: 'Breaker',
    kind: 'humanoid',
    movement: {
      maxRunSpeedMul: 0.82,
      groundAccelMul: 0.85,
      massMul: 1.4,
      maxFallSpeedMul: 1.15,
      passesLooseSolids: true,
      dashSpeedMul: 0.9,
    },
    innateAbilities: ['breach'],
    survivability: surv({ hpMul: 1.25, poiseMul: 1.6 }),
    systemicBias: {
      damageTaken: { kinetic: 0.75, corrosive: 1.2 },
      statusTaken: { stagger: 0.5 },
      refuses: [],
      note: 'Mass buys poise and pays for it in cornering.',
    },
    special: 'Breach from minute zero — routes are made, not found. Walks through loose cells.',
  },
  {
    id: 'scavenger',
    name: 'Scavenger',
    kind: 'humanoid',
    movement: {
      maxRunSpeedMul: 1.12,
      groundAccelMul: 1.1,
    },
    innateAbilities: [],
    survivability: surv({ hpMul: 0.75 }),
    systemicBias: {
      damageTaken: { kinetic: 1.15 },
      statusTaken: {},
      refuses: [],
      note: 'Fast and fragile — mistakes cost the whole margin.',
    },
    special: 'Extra dash charge and halved fall damage; falls are a route, not a hazard.',
  },
  {
    id: 'hunter',
    name: 'Hunter',
    kind: 'humanoid',
    movement: {
      canWallCling: true,
      dashSpeedMul: 0.85,
      dashDurationMul: 1.1,
      jumpHeightMul: 1.18,
      airAccelMul: 1.1,
    },
    innateAbilities: ['cling'],
    survivability: surv({}),
    systemicBias: {
      damageTaken: {},
      statusTaken: {},
      // Cling's material rule still applies — an oiled shaft is a real wall.
      refuses: ['SLICK'],
      note: 'Vertical space is home; slick walls take it away.',
    },
    special: 'Wall-cling from start, longer jump, slower dash — vertical shafts read as corridors.',
  },
  {
    id: 'techno-knight',
    name: 'Techno-knight',
    kind: 'humanoid',
    movement: {
      verticalMode: 'hover-thrust',
      thermalSeconds: 2.2,
      massMul: 1.2,
      maxRunSpeedMul: 0.94,
    },
    innateAbilities: [],
    survivability: surv({ hpMul: 1.15, poiseMul: 1.3 }),
    systemicBias: {
      damageTaken: { fire: 1.35, shock: 1.4, cryo: 0.7 },
      statusTaken: { overheat: 1.5, wet: 0.6 },
      refuses: [],
      // Water is both the vent and the short — that tension is the whole body.
      note: 'Vents in water; shorts in water. Read the liquid before you dive.',
    },
    special: 'Hover-thrust replaces the double jump and overheats; water vents the heat.',
  },
  {
    id: 'ritualist',
    name: 'Ritualist',
    kind: 'humanoid',
    movement: {
      maxRunSpeedMul: 0.74,
      groundAccelMul: 0.8,
      airAccelMul: 1.05,
    },
    innateAbilities: ['blink'],
    survivability: surv({ hpMul: 0.9 }),
    systemicBias: {
      damageTaken: { blood: 1.3, void: 0.8 },
      statusTaken: { bleed: 1.4 },
      refuses: [],
      note: 'Blink costs blood, so the HP bar is a traversal resource.',
    },
    special: 'Slowest on foot, Blink from start on a blood cost — mobility is paid for in HP.',
  },
  {
    id: 'berserker',
    name: 'Berserker',
    kind: 'humanoid',
    movement: {
      speedPerMissingHp: 0.35,
      canWallSlide: false,
      turnAccelMul: 1.2,
      dashCooldownMul: 0.85,
    },
    innateAbilities: [],
    survivability: surv({ hpMul: 1.1 }),
    systemicBias: {
      damageTaken: { blood: 0.7 },
      statusTaken: { bleed: 0.6, fear: 0 },
      refuses: [],
      note: 'Being hurt is the build; the loss of wall-slide is the price.',
    },
    special: 'Up to +35% speed at low HP and cannot wall-slide — descent is committal.',
  },
  {
    id: 'chrono-monk',
    name: 'Chrono-monk',
    kind: 'humanoid',
    movement: {
      dashMode: 'rewind',
      // A rewind covers no ground, so gap-crossing must come from the base jump.
      jumpHeightMul: 1.08,
      dashCooldownMul: 1.15,
    },
    innateAbilities: [],
    survivability: surv({}),
    systemicBias: {
      damageTaken: { temporal: 0.5 },
      statusTaken: { slow: 0.4 },
      refuses: [],
      note: 'Rewind undoes position, not damage already resolved.',
    },
    special: 'Dash rewinds you 0.4 s instead of translating — escapes, never advances.',
  },
  {
    id: 'spectral-entity',
    name: 'Spectral entity',
    kind: 'weird',
    movement: {
      passesLooseSolids: true,
      massMul: 0.5,
      fallGravityMul: 0.85,
      groundFrictionMul: 0.6,
    },
    innateAbilities: [],
    survivability: surv({ hpMul: 0.8 }),
    systemicBias: {
      // Verbatim from docs/02 §3.2 — the canonical example of body-as-bias.
      damageTaken: { matter: 0.5, kinetic: 0.5, radiant: 2, sacred: 2 },
      statusTaken: { bleed: 0, burn: 0.5 },
      refuses: ['SACRED'],
      note: 'Half damage from matter, double from radiant. Consecrated ground is lethal terrain.',
    },
    special: 'Always passes loose solids; radiant biomes become a damage-type problem.',
  },
  {
    id: 'floating-relic',
    name: 'Floating relic',
    kind: 'weird',
    movement: {
      verticalMode: 'flight',
      flightSeconds: 3.6,
      groundFrictionMul: 0,
      maxRunSpeedMul: 0.9,
      airAccelMul: 1.25,
      canWallSlide: false,
    },
    innateAbilities: [],
    survivability: surv({ hpMul: 0.85 }),
    systemicBias: {
      damageTaken: { resonant: 1.4, shock: 1.25 },
      statusTaken: { drain: 1.5 },
      refuses: [],
      note: 'Flight fuel is a second health bar; drain effects ground you.',
    },
    special: 'True flight on an energy meter and zero ground friction — landings never settle.',
  },
  {
    id: 'parasite-colony',
    name: 'Parasite colony',
    kind: 'weird',
    movement: {
      bodyCount: 3,
      maxRunSpeedMul: 1.05,
      massMul: 0.6,
      minGapCells: 5,
    },
    innateAbilities: [],
    survivability: surv({
      hpMul: 0.55,
      damageModel: 'per-body',
      revival: {
        kind: 'respawn-body',
        charges: 2,
        scope: 'room',
        note: 'A surviving body regrows a lost one at a rest beat',
      },
    }),
    systemicBias: {
      damageTaken: { fire: 1.5, fungal: 0.3, toxic: 0.4 },
      statusTaken: { burn: 1.6, spore: 0 },
      refuses: [],
      note: 'Fire is the colony killer; splitting means AoE hits everything at once.',
    },
    special: 'Splits into 3 bodies with independent momentum — three platforming problems at once.',
  },
  {
    id: 'semi-liquid-mutant',
    name: 'Semi-liquid mutant',
    kind: 'weird',
    movement: {
      minGapCells: 3,
      slopeBias: 0.65,
      groundFrictionMul: 0.45,
      viscosityImmunity: 0.6,
      jumpHeightMul: 0.9,
      canWallSlide: true,
      wallSlideSpeedMul: 1.3,
    },
    innateAbilities: [],
    survivability: surv({ hpMul: 0.95 }),
    systemicBias: {
      damageTaken: { kinetic: 0.6, cryo: 1.8, corrosive: 0.7 },
      statusTaken: { frozen: 2 },
      refuses: [],
      note: 'Freezing removes the entire movement identity, so cryo is an existential threat.',
    },
    special: 'Squeezes through 3-cell gaps and flows downhill; uphill is genuine work.',
  },
  {
    id: 'possessed-armor',
    name: 'Possessed armor',
    kind: 'weird',
    movement: {
      massMul: 1.55,
      maxRunSpeedMul: 0.88,
      viscosityImmunity: 1,
    },
    innateAbilities: [],
    survivability: surv({ hpMul: 1.3, drowns: false, poiseMul: 1.7 }),
    systemicBias: {
      damageTaken: { fire: 0.4, cryo: 0.8, shock: 1.5, corrosive: 1.6 },
      statusTaken: { drown: 0, burn: 0.2, rust: 1.8 },
      refuses: [],
      note: 'Cannot drown, cannot swim — water is a floor you walk on the bottom of.',
    },
    special: 'Immune to drowning and heat but sinks; flooded rooms become walking routes.',
  },
  {
    id: 'segmented-biomech',
    name: 'Segmented biomech',
    kind: 'weird',
    movement: {
      segmentCount: 3,
      hitboxHeightMul: 1.6,
      maxRunSpeedMul: 0.95,
      // Tail anchoring is a stretch, not a swing — no pendulum, no fall.
      canWallCling: true,
      // Issue #186: "reach replaces jump distance, and it is aimable" — the
      // grounded jump becomes an 8-way-aimed launch instead of a vertical one.
      tailAnchorReach: true,
    },
    innateAbilities: [],
    survivability: surv({ hpMul: 1.2, damageModel: 'segmented', poiseMul: 1.2 }),
    systemicBias: {
      damageTaken: { corrosive: 1.3, nanite: 1.4 },
      statusTaken: {},
      refuses: [],
      note: 'A long body is a large target; segments are lost head-to-tail.',
    },
    special: 'Anchor the tail and stretch — reach replaces jump distance, and it is aimable.',
  },
  {
    id: 'bone-construct',
    name: 'Bone construct',
    kind: 'weird',
    movement: {
      massMul: 0.85,
      maxRunSpeedMul: 1.02,
    },
    innateAbilities: [],
    survivability: surv({
      hpMul: 0.85,
      revival: {
        kind: 'reassemble',
        charges: 1,
        scope: 'room',
        note: 'Reassembles at 30% HP after a lethal hit, once per room',
      },
    }),
    systemicBias: {
      // Freeze -> shatter is the intended counterplay against bone (docs/03 §6).
      damageTaken: { kinetic: 1.2, radiant: 1.4, toxic: 0.3, bleed: 0 },
      statusTaken: { frozen: 1.8, bleed: 0 },
      refuses: [],
      note: 'Brittle when frozen: kinetic damage while FROZEN bypasses reassembly.',
    },
    special: 'Reassembles once per room after a lethal hit — unless it dies frozen.',
  },
  {
    id: 'modular-drone-swarm',
    name: 'Modular drone swarm',
    kind: 'weird',
    movement: {
      bodyCount: 5,
      minGapCells: 1,
      massMul: 0.35,
      verticalMode: 'hover-thrust',
      flightSeconds: 1.8,
      groundFrictionMul: 0.5,
    },
    innateAbilities: [],
    survivability: surv({ hpMul: 0.9, damageModel: 'shared-pool' }),
    systemicBias: {
      damageTaken: { shock: 2, nanite: 1.7, kinetic: 0.7, bleed: 0 },
      statusTaken: { emp: 2.2, bleed: 0 },
      refuses: [],
      note: 'EMP-fragile by construction (docs/03 §6 drone swarm), but 1-cell gaps are doors.',
    },
    special: 'Discrete drones on a shared HP pool that thread 1-cell gaps — geometry stops mattering.',
  },
  {
    id: 'flesh-machine-hybrid',
    name: 'Flesh-machine hybrid',
    kind: 'weird',
    movement: {
      stances: ['organic', 'machine'],
      // Tuning here is the midpoint; the stance system re-applies its own delta.
      maxRunSpeedMul: 1.0,
      massMul: 1.1,
      thermalSeconds: 3,
    },
    innateAbilities: [],
    survivability: surv({
      hpMul: 1.05,
      revival: {
        kind: 'restance',
        charges: 1,
        scope: 'run',
        note: 'A lethal hit forces a stance swap instead of death, once per run',
      },
    }),
    systemicBias: {
      // Each stance inverts the other's weakness — that is the whole decision.
      damageTaken: { fire: 1.5, shock: 1.5 },
      statusTaken: { burn: 1.4, emp: 1.4 },
      refuses: [],
      note: 'Organic burns, machine shorts. The stance you are in is the weakness you have.',
    },
    special: 'Swaps organic (fast, burns) and machine (armored, shorts) — hazards choose your stance.',
    // Issue #185. Deltas from the midpoint above, not from base — organic biases
    // toward the mobility end of the package the way Scavenger does (+speed,
    // +accel, lighter), machine toward the end Breaker occupies (heavier,
    // slower, more sluggish to turn) — same shapes as those two archetypes'
    // own `movement` deltas, chosen because the card fiction is explicit
    // ("fast, burns" / "armored, shorts") and those two are this repo's
    // existing precedent for what "fast body" and "armored body" cost in
    // `MovementModifiers` terms. `damageTaken`/`statusTaken` stay on the
    // archetype's fixed `systemicBias` rather than moving per stance — that
    // table has no reader yet (see `damageBias`'s own docblock), and giving
    // it per-stance values here would be authoring a second unread axis
    // instead of wiring the one that already exists.
    stanceProfiles: {
      organic: {
        maxRunSpeedMul: 1.15,
        groundAccelMul: 1.15,
        airAccelMul: 1.1,
        massMul: 0.85,
        dashSpeedMul: 1.1,
      },
      machine: {
        maxRunSpeedMul: 0.85,
        groundAccelMul: 0.85,
        massMul: 1.25,
        maxFallSpeedMul: 0.9,
        wallSlideSpeedMul: 0.8,
      },
    },
  },
];

export const ARCHETYPE_IDS: readonly string[] = ARCHETYPES.map((a) => a.id);

export const ARCHETYPE_BY_ID: Readonly<Record<string, ArchetypeDef>> = Object.fromEntries(
  ARCHETYPES.map((a) => [a.id, a]),
);

/** The body you always have. Everything else arrives through Lineage. */
export const DEFAULT_ARCHETYPE = 'duelist';

export function getArchetype(id: string): ArchetypeDef {
  const a = ARCHETYPE_BY_ID[id];
  if (!a) throw new Error(`unknown archetype: ${id}`);
  return a;
}

/** Base package × archetype delta, resolved once at run start. */
export function resolveMovement(id: string): MovementModifiers {
  return { ...BASE_MOVEMENT_MODIFIERS, ...getArchetype(id).movement };
}

/**
 * Issue #185. The named stance's delta, to stack on top of `resolveMovement`'s
 * own result the same way `applyModifiers` is already called twice for a body
 * with flight (once for the archetype, `entity/movementTuning.ts`'s own
 * comment on `MovementModifiers` is where that pattern is stated).
 *
 * A `Partial`, not filled against `BASE_MOVEMENT_MODIFIERS` — filling it would
 * make every neutral field ("no change") indistinguishable from a stance that
 * explicitly re-asserts the base package, and `applyModifiers`'s `mul` helper
 * already treats "absent" as "no change" for every multiplicative field it
 * reads. Returns `{}` for an id with no authored `stanceProfiles`, or a
 * stance id nothing declared — both read as "the midpoint, unmodified."
 */
export function resolveStance(id: string, stanceId: string): Partial<MovementModifiers> {
  return getArchetype(id).stanceProfiles?.[stanceId] ?? {};
}

/**
 * Body bias for one damage class. Absent = 1.0.
 *
 * **Uncalled, and kept uncalled on purpose — #118 judged this one and declined
 * to wire it.** These two are the only readers of `SystemicBias`, and nothing
 * reads them, so the whole struct is authored and unread. Its sibling
 * `Survivability` is not: `core/game.ts` applies `hpMul` and `poiseMul` at
 * spawn. So the bias is the unimplemented half of the body-plan model, not a
 * missing call.
 *
 * The reason not to close it with one wire is `moveSpeedMul`'s, recorded in
 * docs/15 §"`moveSpeedMul` stays dead": wiring it is a balance change wearing a
 * dead-export fix as a disguise. Multiplying incoming damage by 0.75 for the
 * Breaker and 1.15 for the Scavenger is a real re-tune of every fight those
 * bodies have, against numbers nobody has played, with no before/after. And it
 * cannot ship silently either — `runSetupAdapter`'s archetype card is where a
 * player would learn a body takes less kinetic damage, and that card refuses on
 * principle to promise what the game does not do (see its `airDashBonus`
 * clause). So the work is: apply the bias in `combat/resolve.ts`, surface it on
 * the card, and measure both. That is a ticket, not a drive-by.
 */
export function damageBias(id: string, damageClass: string): number {
  const v = getArchetype(id).systemicBias.damageTaken[damageClass];
  return v === undefined ? 1 : v;
}

/** Body bias for one status buildup. Absent = 1.0. See `damageBias` for why neither is called. */
export function statusBias(id: string, status: string): number {
  const v = getArchetype(id).systemicBias.statusTaken[status];
  return v === undefined ? 1 : v;
}
